import React, { useState, useEffect, useRef } from "react";
import { X, Bell, CheckCircle } from "lucide-react";

const NotificationSoundContext = React.createContext({
  playSound: () => {},
  canPlaySound: false,
});

export const NotificationSoundProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [canPlaySound, setCanPlaySound] = useState(false);

  useEffect(() => {
    const audio = new Audio();
    audio.src =
      "data:audio/wav;base64,//uQRAAAAWMSLwUIYAAsYkXgoQwAEaYLWfkWgAI0wWs/ItAAAGDgYtAgAyN+QWaAAihwMWm4G8QQRDiMcCBcH3Cc+CDv/7xA4Tvh9Rz/y8QADBwMWgQAZG/ILNAARQ4GLTcDeIIIhxGOBAuD7hOfBB3/94gcJ3w+o5/5eIAIAAAVwWgQAVQ2ORaIQwEMAJiDg95G4nQL7mQVWI6GwRcfsZAcsKkJvxgxEjzFUgfHoSQ9Qq7KNwqHwuB13MA4a1q/DmBrHgPcmjiGoh//EwC5nGPEmS4RcfkVKOhJf+WOgoxJclFz3kgn//dBA+ya1GhurNn8zb//9NNutNuhz31f////9vt///z+IdAEAAAK4LQIAKobHItEIYCGAExBwe8jcToF9zIKrEdDYIuP2MgOWFSE34wYiR5iqQPj0JIeoVdlG4VD4XA67mAcNa1fhzA1jwHuTRxDUQ//iYBczjHiTJcIuPyKlHQkv/LHQUYkuSi57yQT//uggfZNajQ3Vm38/////+7LrQIAAAKBkLAQAKlQ+BCkpKSklJSMjI//uQZAAACZklMQjUAAAd0cW1Uwk7PQAACIIAAgXMgAAAiAAAAAEDvr+/LPwz/rAGAAAAAAAAAAAAAAAAAAAAAAAAAYAAAGAIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/9k=";
    audioRef.current = audio;

    const enableSound = () => {
      setCanPlaySound(true);
      audio.volume = 0;
      audio.play().catch(() => {});
      audio.volume = 1;
    };

    document.addEventListener("click", enableSound, { once: true });
    document.addEventListener("keydown", enableSound, { once: true });
    document.addEventListener("touchstart", enableSound, { once: true });

    return () => {
      document.removeEventListener("click", enableSound);
      document.removeEventListener("keydown", enableSound);
      document.removeEventListener("touchstart", enableSound);
    };
  }, []);

  const playSound = () => {
    if (audioRef.current && canPlaySound) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.log("Could not play notification sound:", err);
      });
    }
  };

  return (
    <NotificationSoundContext.Provider value={{ playSound, canPlaySound }}>
      {children}
    </NotificationSoundContext.Provider>
  );
};

const PushNotification = ({
  title,
  message,
  type = "info",
  autoClose = false,
  duration = 5000,
  onClose,
  playSound = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const { playSound: playSoundFn, canPlaySound } = React.useContext(
    NotificationSoundContext
  );

  useEffect(() => {
    if (playSound && canPlaySound) {
      playSoundFn();
    }

    let interval;
    let timeout;

    if (autoClose) {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prev - 100 / (duration / 100);
        });
      }, 100);

      timeout = setTimeout(() => {
        setIsVisible(false);
        if (onClose) setTimeout(() => onClose(), 300);
      }, duration);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [duration, onClose, autoClose, playSound, playSoundFn, canPlaySound]);

  if (!isVisible) return null;

  const typeStyles = {
    info: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-800",
      icon: <Bell size={18} className="text-blue-500" />,
      progress: "bg-blue-500",
    },
    success: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-800",
      icon: <CheckCircle size={18} className="text-green-500" />,
      progress: "bg-green-500",
    },
    warning: {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      text: "text-yellow-800",
      icon: <Bell size={18} className="text-yellow-500" />,
      progress: "bg-yellow-500",
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-800",
      icon: <Bell size={18} className="text-red-500" />,
      progress: "bg-red-500",
    },
  };

  const styles = typeStyles[type] || typeStyles.info;

  return (
    <div
      className={`mb-3 rounded-lg shadow-lg ${styles.bg} border ${
        styles.border
      } ${styles.text} transition-all ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
      role="alert"
    >
      <div className="flex p-4">
        <div className="flex-shrink-0 mr-3 mt-0.5">{styles.icon}</div>
        <div className="flex-1">
          {title && <h3 className="font-medium mb-1">{title}</h3>}
          <div className="text-sm">{message}</div>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            if (onClose) setTimeout(() => onClose(), 300);
          }}
          className={`flex-shrink-0 ml-2 ${styles.text} opacity-60 hover:opacity-100`}
        >
          <X size={16} />
        </button>
      </div>
      {autoClose && (
        <div className="w-full bg-gray-200 h-1 rounded-b-lg overflow-hidden">
          <div
            className={`${styles.progress} h-full transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

const PushNotificationManager = ({ notifications = [], onClose }) => {
  if (!notifications.length) return null;

  const containerStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 9999,
    maxWidth: "400px",
    width: "auto",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div style={containerStyle}>
      {notifications.map((notification, index) => (
        <PushNotification
          key={notification.id || index}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          autoClose={notification.autoClose}
          duration={notification.duration}
          playSound={notification.playSound}
          onClose={() => onClose(notification.id || index)}
        />
      ))}
    </div>
  );
};

export { PushNotification, PushNotificationManager };
